import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { CheckCircle2, XCircle, ShieldCheck, Clock, UserCheck } from "lucide-react";

export type ReviewQuestion = {
  id: number;
  text: string;
  options: string[];
  correctOptionIndex: number;
  explanation: string;
  category: string;
  difficulty: string | null;
  licenseTrack: string;
  publishStatus: string;
  reviewApprovals?: number;
  reviewRejections?: number;
  reviews?: { id: number; name: string; email: string; decision: string }[];
};

type ReviewQueueResponse = {
  enabled: boolean;
  reviewersRequired: 1 | 2;
  total: number;
  page: number;
  limit: number;
  questions: ReviewQuestion[];
};

async function fetchWithAuth(path: string, options?: RequestInit) {
  const baseUrl = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
  const token = localStorage.getItem("token");
  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export function QuestionReviewPanel({
  activeTrack,
  userRole,
  userId,
}: {
  activeTrack: string;
  userRole: string;
  userId: number;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const isMaster = userRole === "master_admin";
  const canReview = ["admin", "master_admin", "moderator"].includes(userRole);

  const { data, isLoading } = useQuery<ReviewQueueResponse>({
    queryKey: ["question-review-queue", activeTrack, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: "30", track: activeTrack });
      return fetchWithAuth(`/api/admin/questions/review-queue?${params}`);
    },
    refetchInterval: 20_000,
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: "approve" | "reject" }) =>
      fetchWithAuth(`/api/admin/questions/${id}/review`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      }),
    onSuccess: (res: { publishStatus: string; published: boolean }) => {
      qc.invalidateQueries({ queryKey: ["question-review-queue"] });
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      qc.invalidateQueries({ queryKey: ["question-bank-stats"] });
      qc.invalidateQueries({ queryKey: ["track-bank-counts"] });
      toast({
        title: res.published ? "Question published" : res.publishStatus === "rejected" ? "Question rejected" : "Review recorded",
        description: res.published
          ? "This question is now live in practice and exam modes."
          : undefined,
      });
    },
    onError: (e: unknown) =>
      toast({ title: "Review failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const publishMutation = useMutation({
    mutationFn: (id: number) => fetchWithAuth(`/api/admin/questions/${id}/publish`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["question-review-queue"] });
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      qc.invalidateQueries({ queryKey: ["question-bank-stats"] });
      qc.invalidateQueries({ queryKey: ["track-bank-counts"] });
      toast({ title: "Published", description: "Question is now visible in practice and exam modes." });
    },
    onError: (e: unknown) =>
      toast({ title: "Publish failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  if (!data?.enabled) {
    return (
      <Card className="border-border">
        <CardContent className="p-10 text-center space-y-2">
          <ShieldCheck className="w-10 h-10 text-muted-foreground/40 mx-auto" />
          <p className="text-sm font-mono text-muted-foreground">
            Question review is off. New imports go live immediately.
          </p>
          {isMaster && (
            <p className="text-xs text-muted-foreground">
              Turn on review in the settings above when you have admins or moderators to approve uploads.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  const required = data.reviewersRequired;
  const totalPages = Math.max(1, Math.ceil(data.total / data.limit));

  return (
    <div className="space-y-4">
      <p className="text-xs font-mono text-muted-foreground">
        {required === 2
          ? "Requires 2 approvals from staff (admin, master admin, or moderator)."
          : "Requires 1 approval from staff (admin, master admin, or moderator)."}
        {isMaster && " You can approve, reject, or use Publish now."}
      </p>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : !data.questions.length ? (
        <Card className="border-border">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="w-10 h-10 text-primary/50 mx-auto mb-3" />
            <p className="text-sm font-mono text-muted-foreground">No questions waiting for review.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.questions.map((q) => {
            const expanded = expandedId === q.id;
            const approvals = q.reviewApprovals ?? 0;
            const alreadyReviewed = q.reviews?.some((r) => r.id === userId);
            return (
              <Card key={q.id} className="border-border">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground line-clamp-2">{q.text}</p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className="font-mono text-xs">
                          {q.category}
                        </Badge>
                        <Badge className="font-mono text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                        <Badge variant="outline" className="font-mono text-xs">
                          <UserCheck className="w-3 h-3 mr-1" />
                          {approvals}/{required} approved
                        </Badge>
                        <span className="text-xs font-mono text-muted-foreground">#{q.id}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setExpandedId(expanded ? null : q.id)}>
                        {expanded ? "Less" : "Details"}
                      </Button>
                      {isMaster && (
                        <Button
                          size="sm"
                          className="gap-1"
                          disabled={publishMutation.isPending}
                          onClick={() => publishMutation.mutate(q.id)}
                        >
                          <ShieldCheck className="w-3.5 h-3.5" />
                          Publish now
                        </Button>
                      )}
                      {canReview && !alreadyReviewed && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-primary border-primary/40"
                            disabled={reviewMutation.isPending}
                            onClick={() => reviewMutation.mutate({ id: q.id, decision: "approve" })}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive border-destructive/40"
                            disabled={reviewMutation.isPending}
                            onClick={() => reviewMutation.mutate({ id: q.id, decision: "reject" })}
                          >
                            <XCircle className="w-3.5 h-3.5 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {canReview && alreadyReviewed && (
                        <span className="text-xs font-mono text-muted-foreground self-center">You reviewed</span>
                      )}
                    </div>
                  </div>
                  {expanded && (
                    <div className="text-xs font-mono space-y-2 border-t border-border pt-3 text-muted-foreground">
                      {q.options.map((o, i) => (
                        <p key={i} className={cn(i === q.correctOptionIndex && "text-primary")}>
                          {String.fromCharCode(65 + i)}. {o}
                          {i === q.correctOptionIndex ? " ✓" : ""}
                        </p>
                      ))}
                      <p className="text-foreground/80 pt-1">{q.explanation}</p>
                      {q.reviews && q.reviews.length > 0 && (
                        <p className="pt-1">
                          Reviews:{" "}
                          {q.reviews.map((r) => `${r.name} (${r.decision})`).join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span className="text-xs font-mono self-center text-muted-foreground">
            Page {page} / {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
